import React from 'react'
import { UseFormSetError, UseFormSetValue } from 'react-hook-form'

import { DataService } from 'api/DataService'
import { constructorProducts, Products } from 'models/Plans'
import moment from 'moment'
import { ChargeRecurrenceBodyModalForm } from 'templates/Charge/interface'
import { v4 as uuidv4 } from 'uuid'

import {
  ALL_CHARGE_TOKENS,
  RECURRENCE_TOKENS
} from 'components/ChargeVariablePicker/chargeVariables'
import { InternalIdentifier } from 'components/IdentifyInternal/IdentifyInternal.interface'
import { enumNature } from 'components/Modal/ModalInvoice/ModalInvoice.utils'

import { formatCents, formatMoney, onlyNumber } from 'utils'
import { priceConverter } from 'utils/FormatMoney'
import { setValues } from 'utils/SetValues'

import { DiscountField } from './Components/Discounts/Discounts.interface'

export interface ConfigurePlanProps {
  plan: string | undefined
  planDefault?: Products
  replaceDiscounts: (discounts: DiscountField[]) => void
  replaceInternalIdentifiers: (identifiers: InternalIdentifier[]) => void
  setValue: UseFormSetValue<ChargeRecurrenceBodyModalForm>
  setIsUnique: React.Dispatch<React.SetStateAction<boolean>>
  setIsCharge?: React.Dispatch<React.SetStateAction<boolean>>
  item?: boolean
  isEdit?: boolean
  currentDescription?: string // Adicionar descrição atual para verificar se deve ser sobrescrita
  isProductOrLink?: boolean // Adicionar flag para indicar se é produto/link

  forceRecurrenceBehavior?: boolean
}

const frequencyMapToNumber = {
  weekly: 0,
  biweekly: 5,
  monthly: 1,
  bimonthly: 6,
  quarterly: 2,
  semester: 3,
  annual: 4
}

// Função para obter produto da API quando necessário
export const getProductByUuid = async (uuid: string): Promise<Products> => {
  if (uuid == 'initial') return {} as Products

  const response = await DataService({
    type: 'GET',
    url: `/v2/products/${uuid}/form`
  })

  return constructorProducts([response.data.record])[0]
}

export const configurePlan = async ({
  plan,
  planDefault,
  replaceDiscounts,
  replaceInternalIdentifiers,
  setValue,
  item,
  setIsUnique,
  isEdit = false, // Valor padrão para isEdit
  setIsCharge,
  currentDescription,
  isProductOrLink = false
}: ConfigurePlanProps) => {
  // Se estiver no modo de edição, não configura o plano
  if (isEdit) return

  if (plan) {
    if (plan === 'initial') return
    let planSelected = await getProductByUuid(plan)

    if (planDefault) {
      planSelected = planDefault
    }

    // Calcular data de vencimento primeiro para usar no cálculo dos descontos
    let dueDate = moment()
    // Quando o vencimento do plano é "no dia da contratação"
    // (immediate_charge / immediate_charge_and_specified_charge), o primeiro
    // vencimento é hoje. O `dueDay` gravado nesses casos é apenas o dia em que a
    // configuração foi salva e não deve inicializar o campo de vencimento.
    const firstDueOnContractDay =
      planSelected?.dueType === 'immediate_charge' ||
      planSelected?.dueType === 'immediate_charge_and_specified_charge' ||
      planSelected?.dueType ===
        'immediate_charge_and_same_month_specified_charge'
    if (planSelected?.dueDay && !firstDueOnContractDay) {
      // Se o due_type for "delay_charge", soma os dias à data atual
      if (planSelected?.dueType === 'delay_charge') {
        dueDate = moment().add(planSelected.dueDay, 'days')
      } else {
        // Lógica original para outros tipos (specified_charge, etc.)
        dueDate = moment().date(planSelected?.dueDay)
        if (moment() > dueDate) {
          dueDate = moment()
            .date(planSelected?.dueDay)
            .month(moment().get('month') + 1)
        }
      }
    }

    // A configuração avançada de ciclos do desconto só existe em recorrência e em
    // produto/link — numa cobrança avulsa o formulário nem mostra os campos, então
    // o plano não deve arrastar ciclo inicial/quantidade para lá.
    const hasDiscountCycles = isProductOrLink || planSelected?.totalCycles !== 1

    const discounts = (planSelected?.discounts || [])?.map((discount) => {
      const amount =
        discount.amount.split('.')[1].length == 1
          ? `${discount.amount}0`
          : discount.amount

      // Acima de 12 o select usa a opção "Outros" (13) e o valor real vai no
      // campo custom.
      const planTotalCycles = hasDiscountCycles ? discount.totalCycles ?? 0 : 0
      const cyclesFields = {
        totalCycles: planTotalCycles > 12 ? 13 : planTotalCycles,
        totalCyclesCustom:
          planTotalCycles > 12 ? planTotalCycles.toString() : '',
        startCycle: hasDiscountCycles ? discount.startCycle ?? 1 : 1
      }

      // Para produtos/links sempre usar days, para criação sempre usar date, para edição de recorrência usar days
      if (isProductOrLink) {
        // Para produtos/links: sempre usar days diretamente
        return {
          amount,
          days: discount.days.toString(), // Usar days do produto diretamente
          uuid: uuidv4(),
          type: discount?.policy === 'percentage' ? '0' : '1',
          ...cyclesFields
        }
      } else {
        // Para criação (cobranças e recorrências): sempre calcular data do desconto baseado no vencimento
        const discountDate = dueDate.clone().subtract(discount.days, 'days')
        // A recorrência mostra o select de dias antes do vencimento e envia `days`.
        // Sem ele o select ficava vazio e o envio recalculava os dias pela data acima,
        // então trocar o 1º vencimento transformava "no dia do vencimento" em 60 dias
        // (SC-11660). A cobrança avulsa continua usando a `date`.
        return {
          amount,
          date: discountDate.toDate(),
          days: discount.days.toString(),
          uuid: uuidv4(),
          type: discount?.policy === 'percentage' ? '0' : '1',
          ...cyclesFields
        }
      }
    })

    // Usar a data de vencimento já calculada acima
    const dateRecurrence = dueDate

    // Configurar descontos usando useFieldArray
    replaceDiscounts(discounts)

    // Os campos de ciclo são globais no formulário (valem para todos os descontos)
    // e são a fonte primária no monte do payload — sem isso a configuração
    // avançada de ciclos do plano se perde.
    if (discounts.length > 0) {
      const [firstDiscount] = discounts
      setValue('discountTotalCycles', firstDiscount.totalCycles)
      setValue('discountTotalCyclesCustom', firstDiscount.totalCyclesCustom)
      setValue('discountStartCycle', firstDiscount.startCycle)
    }

    // Processar identificadores internos do produto
    if (planSelected?.metadata) {
      const metadata: InternalIdentifier[] = []
      Object.keys(planSelected.metadata).forEach((key) => {
        if (key && planSelected.metadata?.[key] !== undefined) {
          metadata.push({
            name: key,
            value: planSelected.metadata[key]
          })
        }
      })

      // IMPORTANTE: Definir hasIdentifyInternal ANTES de chamar replaceInternalIdentifiers
      // para evitar que o componente IdentifyInternal remova os campos
      if (metadata.length > 0) {
        setValue('hasIdentifyInternal', true)

        // Aguardar um tick para garantir que o React processe a mudança
        setTimeout(() => {
          replaceInternalIdentifiers(metadata)
        }, 0)
      }
    }

    const parseAmount = onlyNumber(formatCents(planSelected?.amount))

    const planAdjustment = planSelected?.adjustment_scheduled

    setValues(
      {
        amountBilled: parseAmount,
        dueDate: new Date(dateRecurrence.toLocaleString()),
        discountPolicy:
          planSelected?.discountPolicy !== 'no_discount' ? true : false,
        interestPolicy:
          planSelected?.interestPolicy !== 'no_interest' ? true : false,
        totalCycles:
          planSelected?.totalCycles === 1
            ? 1
            : planSelected?.totalCycles > 12
            ? 13 // Se for maior que 12, usa 13 (Outros)
            : planSelected?.totalCycles,
        // Se totalCycles > 12, define totalCyclesCustom com o valor real
        ...(planSelected?.totalCycles > 12 && {
          totalCyclesCustom: planSelected?.totalCycles.toString()
        }),
        frequency:
          frequencyMapToNumber[
            planSelected?.frequency as keyof typeof frequencyMapToNumber
          ],
        // Configurar payments baseado em paymentTypeDetails (preferencial) ou paymentType legado
        payments: (() => {
          const ptd = planSelected?.paymentTypeDetails
          if (ptd) {
            const arr: string[] = []
            if (ptd.pix?.enabled) arr.push('pix')
            if (ptd.boleto?.enabled) arr.push('boleto')
            // Adicionar credit_card ao array se estiver habilitado no nível principal
            if (ptd.credit_card?.enabled) arr.push('credit_card')
            return arr
          }
          return planSelected?.paymentType
        })(),
        // Definir payment_type_details quando disponível
        payment_type_details: planSelected?.paymentTypeDetails || undefined,
        // Só define a descrição se a atual estiver vazia
        // Para produtos/links, usar billingDescription; para cobranças/recorrências, usar description
        ...((!currentDescription || !currentDescription.trim()) && {
          [isProductOrLink ? 'billingDescription' : 'description']:
            planSelected?.billingDescription || ''
        }),

        // Configurar tags do produto se existirem. As tags do plano valem tanto para
        // a cobrança (campo raiz) quanto para o item que originou a seleção — este
        // fluxo só roda com o plano no primeiro item (ver SharedChargeFields), então
        // `items.0.tags` é o escopo correspondente. Seleções feitas manualmente no
        // autocomplete são tratadas em ItemRow (applyProductTags).
        ...(planSelected?.tags &&
          planSelected.tags.length > 0 && {
            tags: planSelected.tags,
            ...(!isProductOrLink && {
              'items.0.tags': planSelected.tags
            })
          }),

        // Configurações de vencimento do produto
        chargeType: planSelected?.chargeType === 'recurrent' ? 1 : 0,
        dueType: planSelected?.dueType || 'immediate_charge',
        // Em "no dia da contratação" o `dueDay` gravado é só o dia em que a
        // configuração foi salva — não inicializa o campo, deixa cair no padrão
        // (dia da dueDate, que é hoje). Demais tipos mantêm o dia configurado.
        dueDay:
          planSelected?.dueType === 'immediate_charge'
            ? undefined
            : planSelected?.dueDay || 1,
        proratedCharge: planSelected?.proratedCharge || false,

        interestFee: planSelected?.interestFee || '1.0',
        interestFine: planSelected?.interestFine || '2.0',
        hasItemsOnDescription: false,
        // Configurações de emissão da fatura. Em "Dia específico do mês" o
        // `issue_days` do plano é o dia do mês, e o envio lê esse dia de
        // `specificDayOfMonth` (sem ele a recorrência saía com o dia 1).
        hasIssueDays: !!planSelected?.issue_days,
        issueDays: planSelected?.issue_days || null,
        issueBillingType:
          planSelected?.issue_billing_type || 'days_before_due_date',
        ...(planSelected?.issue_billing_type === 'specific_day' && {
          specificDayOfMonth: Number(planSelected?.issue_days) || 1
        }),

        // Configurações de parcelamento do produto - preferir paymentTypeDetails
        max_installments:
          planSelected?.paymentTypeDetails?.credit_card?.max_installments ??
          planSelected?.productInstallment?.maxInstallments ??
          12,
        max_installments_without_fee: (() => {
          const maxInst =
            planSelected?.paymentTypeDetails?.credit_card?.max_installments ??
            planSelected?.productInstallment?.maxInstallments ??
            12
          const maxInstWithoutFee =
            planSelected?.paymentTypeDetails?.credit_card
              ?.max_installments_without_fee ??
            planSelected?.productInstallment?.maxInstallmentsWithoutFee ??
            12

          // Se os valores forem iguais, retornar null (switch desligado)
          return maxInst === maxInstWithoutFee ? null : maxInstWithoutFee
        })(),
        pass_card_fees: (() => {
          return (
            planSelected?.paymentTypeDetails?.credit_card?.pass_card_fees ??
            false
          )
        })(),
        // Configurar bonificação do produto
        hasBonus: planSelected?.hasBonus || false,
        bonusSettings: planSelected?.hasBonus
          ? {
              cyclesCount: planSelected.bonusSettings?.cyclesCount || 1,
              type: planSelected.bonusSettings?.type || 'value',
              // Corrigir o formato do amount para evitar problemas de conversão
              amount:
                planSelected.bonusSettings?.type === 'value'
                  ? formatCents(planSelected.bonusSettings?.amount || '')
                  : planSelected.bonusSettings?.amount || '',
              cyclesCountCustom:
                (planSelected.bonusSettings?.cyclesCount || 0) > 12
                  ? (planSelected.bonusSettings?.cyclesCount || 0).toString()
                  : ''
            }
          : undefined,

        // Configurar reajuste do produto. A data gravada no molde está ancorada no
        // dia em que o plano foi salvo, então serve só de semente: o
        // AdjustmentAccordion recalcula a partir do vencimento na criação
        // (mesma regra do backend em `build_adjustment_scheduled_for`).
        hasAdjustment: !!planAdjustment,
        ...(planAdjustment && {
          adjustmentSettings: {
            adjustment_type: planAdjustment.adjustment_type || '',
            frequency: planAdjustment.frequency || 'annual',
            percentage_value:
              planAdjustment.adjustment_type === 'customized' &&
              planAdjustment.percentage_value !== null &&
              planAdjustment.percentage_value !== undefined
                ? String(planAdjustment.percentage_value)
                : '',
            next_adjustment_date: planAdjustment.next_adjustment_date || '',
            automatic_application: planAdjustment.automatic_application ?? false
          }
        })
      } as any,
      setValue
    )

    const isUniqueValue = planSelected?.totalCycles === 1 ? true : false
    setIsUnique(isUniqueValue)
    if (setIsCharge) {
      setIsCharge(isUniqueValue)
    }
    if (planSelected.nfsePolicy != 'no_nfse') {
      setValue('interestInvoice', true)
      setValue('amount_type', planSelected.nfseScheduled?.amount_type)
      setValue('copy_description', planSelected.nfseScheduled?.copy_description)
      setValue('descriptionInvoice', planSelected.nfseScheduled?.description)
      if (
        planSelected.nfseScheduled?.amount_type === 'amount_custom' &&
        planSelected.nfseScheduled?.amount
      ) {
        setValue(
          'amountCustom',
          formatCents(planSelected.nfseScheduled.amount.toString())
        )
      }
      setValue(
        'iss_retention',
        planSelected.nfseScheduled?.iss_retention ? 'yes' : 'no'
      )
      const isCustomPolicy =
        planSelected.nfsePolicy === 'on_before_due_date' ||
        planSelected.nfsePolicy === 'on_after_due_date'
      setValue(
        'issue_when',
        isCustomPolicy
          ? 'on_custom_issued'
          : planSelected.nfseScheduled?.issue_when || planSelected.nfsePolicy
      )
      if (isCustomPolicy) {
        setValue(
          'due_date_period',
          planSelected.nfsePolicy as 'on_before_due_date' | 'on_after_due_date'
        )
        if (planSelected.nfseScheduled?.days) {
          setValue('days', planSelected.nfseScheduled.days.toString())
        }
      }

      setValue(
        'nature_operation',
        enumNature[
          planSelected.nfseScheduled
            ?.nature_operation as keyof typeof enumNature
        ]
      )

      // Definir prestador de serviço do produto
      if (planSelected.nfseIssuerUuid) {
        setValue('nfse_issuer_uuid', planSelected.nfseIssuerUuid)
      }
      // Definir código de serviço do produto
      if (planSelected.nfseScheduled?.nfse_issuer_service?.uuid) {
        setValue(
          'service_list_code',
          planSelected.nfseScheduled.nfse_issuer_service.uuid
        )
      }
    }

    // Configurar negativação do produto
    if (
      planSelected?.negativation?.days_to_report !== undefined &&
      planSelected?.negativation?.days_to_report !== null
    ) {
      setValue('interestNegativation', true)
      setValue(
        'daysToReport',
        planSelected.negativation.days_to_report.toString()
      )
    }

    // Configurar expiração da cobrança do produto
    if (
      planSelected?.limit_days_for_payment !== undefined &&
      planSelected?.limit_days_for_payment !== null
    ) {
      setValue(
        'expirationField',
        planSelected.limit_days_for_payment.toString()
      )
    }

    if (!item) {
      setValue('items', [
        {
          uuid: '',
          price: parseAmount as unknown as number,
          quantity: 1,
          total: parseAmount as unknown as number,
          typedValue: '',
          product: {
            label: planSelected.name,
            value: planSelected.uuid,
            total_cycles: planSelected.totalCycles
          },
          enable_customer_select_quantity: false
        }
      ])
    }
  }
}

export const checkDescriptionVariant = (
  description: string | undefined,
  setError: UseFormSetError<ChargeRecurrenceBodyModalForm>,
  isRecurrence: boolean
) => {
  if (!description) return true

  // Extrai todas as ocorrências de {{...}} (campo_extra pode conter aspas)
  const tokens = description.match(/\{\{[^{}]*\}\}/g)

  if (!tokens) return true

  const campoExtraRegex = /^\{\{campo_extra:".+"\}\}$/

  const allValid = tokens.every(
    (token) => ALL_CHARGE_TOKENS.includes(token) || campoExtraRegex.test(token)
  )

  if (!allValid) {
    setError('description', {
      message:
        'Este modelo contém parâmetros de variável com formatação incorreta. Use o botão "Adicionar variável" para inserir uma variável válida.',
      type: 'pattern'
    })

    return false
  }

  // Variáveis de recorrência não são permitidas em cobrança avulsa
  if (!isRecurrence) {
    const hasRecurrenceToken = tokens.some((token) =>
      RECURRENCE_TOKENS.includes(token)
    )

    if (hasRecurrenceToken) {
      setError('description', {
        message: 'Variáveis de recorrência não funcionam em cobrança avulsa.',
        type: 'pattern'
      })

      return false
    }
  }

  return true
}

export const formatedMessageError = (
  rawMessage: string | string[] | null | undefined
): string => {
  // SC-10012: a API devolve lista (errors.full_messages) quando o model recusa,
  // ex.: cartão de crédito não liberado. Sem mensagem, um aviso genérico.
  const errorMessage = Array.isArray(rawMessage)
    ? rawMessage.filter(Boolean).join(' ')
    : String(rawMessage ?? '')
  if (!errorMessage.trim()) return 'Não foi possível concluir. Tente novamente.'

  const detail = errorMessage.split(' - ')[1]
  if (errorMessage.includes('[') && detail) {
    const formatedMessage = detail.replace(/[^a-zA-Z 0-9 . ç]+/g, '').split(' ')

    formatedMessage[formatedMessage.length - 1] = formatMoney(
      formatedMessage[formatedMessage.length - 1]
    )

    return formatedMessage.join(' ')
  }
  return errorMessage
}

export const createTextTable = (
  items: {
    price: number
    quantity: number
    total: number
    description?: string
    product?: { label: string; value: string }
    typedValue: ''
    uuid: string
  }[],
  totalValue: number,
  isRecurrence?: boolean,
  onInvalidItems?: () => void
) => {
  if (!items || items.length === 0) return ''

  let result = `Itens da ${isRecurrence ? 'recorrência' : 'cobrança'}\n\n`
  let hasValidItems = false

  items.forEach((item, index) => {
    if (item.price === 0 && item.total === 0) return

    hasValidItems = true
    const productName = item.product
      ? item.product.label
      : item.description || ''
    result += `Item ${index + 1}: ${productName} / `
    result += `Preço unitário: ${formatMoney(priceConverter(item.price))} / `
    result += `Quantidade: ${item.quantity} / `
    result += `Subtotal: ${formatMoney(priceConverter(item.total))}`
    result += '\n'
  })

  if (!hasValidItems || totalValue === 0) {
    if (onInvalidItems) {
      onInvalidItems()
    }
    return ''
  }

  result += `\nTotal: R$ ${totalValue.toFixed(2)} \n`

  return result
}
